import React from 'react';
import { createPortal } from 'react-dom';

import { AnimatePresence, motion } from 'framer-motion';

import { useDeleteConfirmationStore } from '@/shared/stores/delete-confirmation-store';

export const DeleteConfirmationModal: React.FC = () => {
  const { isOpen, kontextToDelete, closeDeleteModal, confirmDelete } = useDeleteConfirmationStore();

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={closeDeleteModal}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="bg-card rounded-xl border border-border p-6 max-w-md w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <span className="text-red-400 text-lg">🗑️</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Confirm Deletion</h3>
              <p className="text-sm text-muted-foreground">This action cannot be undone</p>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-muted-foreground leading-relaxed">
              Are you sure you want to delete this photo? It will be permanently deleted.
            </p>

            {/* Show image thumbnail if available */}
            {kontextToDelete?.mediaInference?.urlThumbnail && (
              <div className="mt-4 flex justify-center">
                <img
                  src={kontextToDelete.mediaInference.urlThumbnail}
                  alt="Generated content"
                  className="w-20 h-20 rounded-lg object-cover border border-border"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={closeDeleteModal}
              className="flex-1 px-4 py-2.5 bg-card hover:bg-secondary text-white rounded-lg transition-colors border border-border cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer"
            >
              Delete
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};
